import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signupUser } from '../services/api';
import { User, Mail, Lock, AlertCircle, ArrowLeft, Home, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', password: '', address: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill all required fields.');
      return;
    }

    setLoading(true);
    const res = await signupUser(formData);
    setLoading(false);

    if (res.success) {
      alert("Registration successful! You may now login.");
      navigate('/login');
    } else {
      setError(res.message || 'Signup failed.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1514222049077-df903dce311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')" }}>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm"></div>

      <div className="relative sm:mx-auto sm:w-full sm:max-w-md z-10">
        <Link to="/" className="flex justify-center mb-6">
          <div className="bg-white/10 p-4 rounded-full backdrop-blur-md border border-white/20 shadow-xl">
            <Home className="w-10 h-10 text-saffron-400" />
          </div>
        </Link>
        <h2 className="text-center text-3xl font-extrabold text-white drop-shadow-lg">
          Join our Community
        </h2>
        <p className="mt-2 text-center text-sm text-stone-300">
          Create an account to write blogs and share your journey.
        </p>
      </div>

      <motion.div 
        className="relative mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        <div className="bg-white/90 backdrop-blur-xl py-8 px-4 shadow-2xl sm:rounded-3xl sm:px-10 border border-white/20">
          <form className="space-y-6" onSubmit={handleSubmit}>
            
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-start border border-red-200 shadow-inner">
                <AlertCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Full Name *</label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-stone-400" />
                </div>
                <input
                  type="text" required
                  className="focus:ring-2 focus:ring-saffron-500 focus:border-saffron-500 block w-full pl-10 py-3 sm:text-sm border-stone-300 rounded-xl bg-white/70"
                  placeholder="Rahul Singh"
                  value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Email Address *</label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-stone-400" />
                </div>
                <input
                  type="email" required
                  className="focus:ring-2 focus:ring-saffron-500 focus:border-saffron-500 block w-full pl-10 py-3 sm:text-sm border-stone-300 rounded-xl bg-white/70"
                  placeholder="rahul@example.com"
                  value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Address (Optional)</label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-stone-400" />
                </div>
                <input
                  type="text"
                  className="focus:ring-2 focus:ring-saffron-500 focus:border-saffron-500 block w-full pl-10 py-3 sm:text-sm border-stone-300 rounded-xl bg-white/70"
                  placeholder="Bareilly, UP"
                  value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Password *</label>
              <div className="mt-1 relative rounded-xl shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-stone-400" />
                </div>
                <input
                  type="password" required
                  className="focus:ring-2 focus:ring-saffron-500 focus:border-saffron-500 block w-full pl-10 py-3 sm:text-sm border-stone-300 rounded-xl bg-white/70"
                  placeholder="••••••••"
                  value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
              </div>
            </div>

            <div>
              <button
                type="submit" disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-gradient-to-r from-saffron-600 to-saffron-500 hover:from-saffron-700 hover:to-saffron-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-saffron-500 transition-all disabled:opacity-70"
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center border-t border-stone-200 pt-6">
            <p className="text-sm text-stone-600">
              Already have an account?{' '}
              <Link to="/login" className="font-bold text-saffron-600 hover:text-saffron-500 transition-colors">
                Sign in instead
              </Link>
            </p>
            <Link to="/" className="inline-flex flex-row items-center justify-center mt-6 text-sm text-stone-500 hover:text-stone-800 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;
